/**
 * ConfirmDialog — the canonical "are you sure?" affordance.
 *
 * Replaces `window.confirm` everywhere. Native confirms are blocking, ugly,
 * not styleable, and an a11y dead-end on macOS Safari (the dialog message
 * is sometimes not read by VoiceOver). This component is keyboard- and
 * screen-reader-friendly out of the box because it sits on Radix AlertDialog.
 *
 * Two usage shapes are supported:
 *
 *   1. **Controlled**: pass `open` + `onOpenChange` and render the component
 *      in your tree alongside the trigger. Best when the trigger is a button
 *      already in the layout — you control state explicitly.
 *
 *   2. **Imperative** (via `useConfirm()`): when the trigger isn't a sibling
 *      (e.g. confirm fires from a row's action menu nested deep in a list),
 *      grab a `confirm()` async function from the hook and `await` it.
 *
 * @see prototype/sales/customers.html — the archive flow (we replace its window.confirm here)
 */
import * as React from "react";
import { TriangleAlert } from "lucide-react";
import {
  AlertDialogRoot,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogAction,
  AlertDialogCancel,
} from "./alert-dialog.js";
import { Button } from "./button.js";
import { cn } from "../lib/cn.js";

export type ConfirmIntent = "default" | "danger";

export interface ConfirmDialogProps {
  /** Open state — pass with `onOpenChange` for the controlled shape. */
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Title — short, action-oriented (e.g. "Archive customer?"). */
  title: React.ReactNode;
  /** Body — explain what will happen + how to undo (or that it can't be undone). */
  description: React.ReactNode;
  /** Action button label. Defaults to "Confirm". */
  confirmLabel?: string;
  /** Cancel button label. Defaults to "Cancel". */
  cancelLabel?: string;
  /**
   * Called when the user clicks confirm. May return a promise — the action
   * button shows the `pending` state until it resolves, and the dialog
   * stays open so the user sees the result. Throw to keep the dialog open
   * with the user's input intact (useful for server-side validation).
   */
  onConfirm: () => void | Promise<void>;
  /**
   * Optional hero media shown CENTERED above the title — a larger illustration
   * (not the small left-aligned danger glyph). When set, the header switches to
   * a centered column layout. Use a muted/monochrome SVG so it reads as
   * decoration, not a status icon.
   */
  media?: React.ReactNode;
  /**
   * Optional small print under the body — e.g. how the payment is processed.
   * Rendered muted, above the action row.
   */
  footnote?: React.ReactNode;
  /**
   * Render variant. `'danger'` paints the action button red — use for
   * destructive operations the user can't easily undo (delete, void,
   * step-up flows like bank-detail change).
   */
  intent?: ConfirmIntent;
  /**
   * External pending flag — set when the parent already tracks the
   * mutation's pending state (e.g. via TanStack Query's `isPending`).
   * Either source (this prop or the awaitable `onConfirm`) flips the
   * action button into the "pending" appearance; whichever races first.
   */
  pending?: boolean;
}

/**
 * Controlled component. Mount once near the trigger, drive `open` from
 * local state. The action button is the autofocus target for keyboard
 * users; the cancel button gets focus first if the dialog is destructive
 * (per shadcn convention; aligns with native AlertDialog focus order).
 */
export function ConfirmDialog({
  open,
  onOpenChange,
  title,
  description,
  confirmLabel = "Confirm",
  cancelLabel = "Cancel",
  onConfirm,
  media,
  footnote,
  intent = "default",
  pending: externalPending = false,
}: ConfirmDialogProps) {
  const [internalPending, setInternalPending] = React.useState(false);
  const isPending = externalPending || internalPending;

  const handleConfirm = async () => {
    setInternalPending(true);
    try {
      await onConfirm();
      onOpenChange(false);
    } catch {
      // Caller's error handling owns the user-visible message; we just keep
      // the dialog open so the user can retry or cancel.
    } finally {
      setInternalPending(false);
    }
  };

  return (
    <AlertDialogRoot open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          {media ? (
            // Centered hero layout — large illustration above the title.
            <div className="flex flex-col items-center text-center">
              <div aria-hidden className="mb-3 text-line-2">
                {media}
              </div>
              <AlertDialogTitle>{title}</AlertDialogTitle>
              <AlertDialogDescription asChild className="mt-1.5">
                {/* `asChild` over a div, not the default <p>: `description` is a
                    ReactNode and callers legitimately pass blocks (alerts, lists).
                    A <div> inside the <p> Radix renders by default is invalid
                    HTML and React rejects it at runtime. The div still carries
                    the description id, so aria-describedby is unaffected. */}
                <div>{description}</div>
              </AlertDialogDescription>
            </div>
          ) : (
            <div className="flex items-start gap-3">
              {intent === "danger" ? (
                <span
                  aria-hidden
                  className={cn(
                    "inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full",
                    "bg-bad-50 text-bad",
                  )}
                >
                  <TriangleAlert size={16} strokeWidth={2.4} />
                </span>
              ) : null}
              <div className="flex-1 min-w-0">
                <AlertDialogTitle>{title}</AlertDialogTitle>
                <AlertDialogDescription asChild className="mt-1.5">
                {/* `asChild` over a div, not the default <p>: `description` is a
                    ReactNode and callers legitimately pass blocks (alerts, lists).
                    A <div> inside the <p> Radix renders by default is invalid
                    HTML and React rejects it at runtime. The div still carries
                    the description id, so aria-describedby is unaffected. */}
                <div>{description}</div>
              </AlertDialogDescription>
              </div>
            </div>
          )}
        </AlertDialogHeader>
        {footnote ? (
          <p className={cn("text-xs leading-relaxed text-muted", media ? "text-center" : "")}>
            {footnote}
          </p>
        ) : null}
        <AlertDialogFooter>
          <AlertDialogCancel asChild>
            <Button intent="ghost" disabled={isPending}>
              {cancelLabel}
            </Button>
          </AlertDialogCancel>
          <AlertDialogAction asChild>
            <Button
              intent={intent === "danger" ? "danger" : "primary"}
              disabled={isPending}
              onClick={(e) => {
                // Prevent Radix's auto-close so we can drive it from
                // the await above (so async failures keep the dialog open).
                e.preventDefault();
                void handleConfirm();
              }}
            >
              {isPending ? "Working…" : confirmLabel}
            </Button>
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialogRoot>
  );
}
